import { GetServerSideProps } from 'next';
import { guia } from '@/services/api';
import Head from 'next/head';
import ImageComponent from '@/components/Layout/Image';
import Chip from '@/components/Layout/Chip';
import Accordion from '@/components/Layout/Accordion';
import ProsAndCons from '@/components/Layout/ProsAndCons';
import IFrameYoutube from '@/components/Layout/IFrameYoutube';
import Link from 'next/link';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useEffect, useState } from 'react';
import classNames from '@/utils/className';
import OfferBanner from '@/components/Internals/OfferBanner';
import CollapsedText from '@/components/Internals/CollapsedText';
import Layout from '@/components/Layout';
import Icon from '@/components/Layout/Icon';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

type LinkKey = 'amazon' | 'magalu' | 'mercado_livre';

interface Produto {
  id: number;
  nome: string;
  imagem: string;
  descricao: string;
  titulo_bloco: string;
  componente_tipo: string;
  componente_conteudo: {
    pros?: string[];
    contras?: string[];
    youtube?: string;
    link_analise?: string;
    imagem?: string;
  };
  links: Partial<Record<LinkKey, string | null>>;
  sort: number;
}

interface Categoria {
  titulo: string;
  produtos: Produto[];
}

interface Itens {
  categorias: Categoria[];
}

interface Banner {
  imagem_desktop: string;
  imagem_mobile: string;
  descricao: string;
}

interface GuiaDeComprasType {
  banner: Banner;
  itens: Itens;
  descricaoPagina: string;
}

interface GuiaDeComprasProps {
  guiaData: GuiaDeComprasType;
}

export default function GuiaDeCompras({ guiaData }: GuiaDeComprasProps) {
  const endpointImages = publicRuntimeConfig.NEXT_PUBLIC_URL_BUCKET;
  const [isActive, setActive] = useState(null);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);

  const handleScroll = () => {
    if (isProgrammaticScroll) {
      return;
    }

    const categoryTitle = document.querySelectorAll('h2[id]');
    let activeSection = '';

    categoryTitle.forEach(h2 => {
      const rect = h2.getBoundingClientRect();
      if (rect.top <= 500) {
        activeSection = h2.id;
      }
    });

    setActive(activeSection);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isProgrammaticScroll]);

  const toggleActive = index => {
    setActive(index);
  };

  const [isTabletOrMobile] = useBreakpoint('tablet');

  const imagensLinks: Record<LinkKey, { title: string; url: string; blurhash: string }> = {
    amazon: {
      title: 'Amazon',
      url: 'https://t.ctcdn.com.br/LykfprPimFYYhJwlIH6oGWKDy5I=/i925931.png',
      blurhash: 'LpO|Lgoex]t7_Nj]oKj?Rjj[RPay',
    },
    magalu: {
      title: 'Magalu',
      url: 'https://t.ctcdn.com.br/iDAAZZNFyTDn0OGcIbDNmqJVbLE=/i925929.png',
      blurhash: 'L,OW:CsoxpkW_NW=n+oJMvbHNKf6',
    },
    mercado_livre: {
      title: 'Mercado Livre',
      url: 'https://t.ctcdn.com.br/h_qiNRYZDHIHDWb8OwUBC4FJLO8=/i925933.png',
      blurhash: 'LzP6]hn#t6og~qofayj[IUf,Rkj?',
    },
  };

  const responsiveClass = 'first:[&>figure]:block last:[&>figure]:hidden tablet:first:[&>figure]:hidden tablet:last:[&>figure]:block';

  return (
    <>
      <Head>
        <title>Guia de Compras</title>
      </Head>
      <Layout>
        <>
          <div className={classNames(responsiveClass, 'w-full p-4 desktop:p-8 [&_figure_div_img]:rounded-xl')}>
            {/* A api precisa mandar os blurhash junto */}
            <ImageComponent src={`${endpointImages}${guiaData.banner.imagem_mobile}`} blurhash="LAAv9C}*ERIcoYxXoKS20B9$-P-i" />

            {/* A api precisa mandar os blurhash junto */}
            <ImageComponent src={`${endpointImages}${guiaData.banner.imagem_desktop}`} blurhash="LE9Z~NvxMa?dSvskwHT14:R;b{M_" height={347} />
          </div>
          <div className="relative mx-auto my-0 block max-w-[1000px] items-center pb-[51px] phone:pb-0 extraLargeDesktop:max-w-[calc(1000px+100vw-1360px)] [@media_only_screen_and_(min-width:1960px)]:max-w-[1600px]">
            <div className="mx-4 desktop:mx-0 desktop:mb-8 [&>div]:border-[1px] [&>div]:border-solid [&>div]:border-mglGreen500 [&>div]:bg-white [&_.canal-ct-wpp]:transition-all [&_.canal-ct-wpp]:duration-300 [&_.canal-ct-wpp]:ease-ease hover:[&_.canal-ct-wpp]:bg-mglGreen500">
              <OfferBanner phrase="🛒 Entre no nosso canal verificado de ofertas no WhatsApp ✅" link="https://canalte.ch/canalofertas" />
            </div>
            <CollapsedText pageDescription={guiaData.descricaoPagina} />
            <div className="grid grid-cols-1 grid-rows-1 gap-0 px-4 desktop:grid-cols-[minmax(0,1fr)_320px] desktop:gap-4 desktop:px-0">
              <div>
                {guiaData.itens.categorias.map(categoria => (
                  <div className="gap-4" id={categoria.titulo.replaceAll(' ', '_')} key={categoria.titulo}>
                    <h2 className="my-6 mt-[3rem] text-lg font-semibold text-mglGray800 desktop:text-3xl" id={categoria.titulo.replaceAll(' ', '_')}>
                      {categoria.titulo}
                    </h2>
                    {categoria.produtos.map(produto => {
                      const availableLinks = (Object.entries(produto.links || {}).filter(([, value]) => Boolean(value)) as [LinkKey, string][]);

                      return (
                        <div className="mb-4 rounded-md border-[0.5px] border-solid border-mglGray200 bg-white p-4 pb-0" key={produto.id}>
                          <div className="mb-4 grid grid-cols-[111px_1fr] grid-rows-1 gap-4 desktop:grid-cols-[184px_1fr] desktop:grid-rows-[3.75rem_1fr] desktop:gap-4">
                            <div className="relative row-span-1 flex max-h-[111px] max-w-[111px] rounded-lg border-[0.5px] border-solid border-mglGray100 p-2 desktop:row-span-2 desktop:max-h-[184px] desktop:max-w-[184px]">
                              <ImageComponent
                                src={`${endpointImages}${produto.imagem}`}
                                width={isTabletOrMobile ? 95 : 168}
                                height={isTabletOrMobile ? 95 : 168}
                                objectFit="object-contain"
                              />
                            </div>
                            <div className="relative col-start-2 col-end-2 flex w-full min-w-0 flex-col justify-center">
                              <span className="text-sm text-mglGray800 desktop:text-base">{produto.titulo_bloco}</span>
                              <h2 className="overflow-hidden truncate text-ellipsis text-lg font-semibold text-mglGray800 desktop:text-2xl" title={produto.nome}>
                                {produto.nome}
                              </h2>
                            </div>
                            {availableLinks.length > 0 ? (
                              <div className="col-span-2 row-span-1 flex w-full flex-col gap-4 desktop:col-span-1 desktop:flex-row">
                                {availableLinks.map(([key, link]) => (
                                  <div
                                    className="flex w-full flex-row items-center justify-center gap-4 rounded-md bg-mglGray100 p-4 desktop:flex-col"
                                    key={key}>
                                    <ImageComponent src={imagensLinks[key].url} blurhash={imagensLinks[key].blurhash} width={100} height={24} alt={imagensLinks[key].title} />
                                    <Chip
                                      title="Ver preço"
                                      texto="Ver preço"
                                      chipSize="medium"
                                      color="white"
                                      hover="black"
                                      backgroundColor="green"
                                      link={link}
                                      className=""
                                      rel="sponsored"
                                      target="_blank"
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>

                          <div
                            className={classNames(
                              'mx-[-1rem] ',
                              '[&>ul]:mx-0 ',
                              '[&>ul_li]:rounded-md [&>ul_li]:py-0 ',
                              '[&>ul_li_button]:mb-0 [&>ul_li_button]:gap-2 [&>ul_li_button]:rounded-b-md [&>ul_li_button]:bg-mglGray100 [&>ul_li_button]:px-4 [&>ul_li_button]:py-4 [&>ul_li_button]:text-mglGray800 ',
                              '[&>ul_li_button_h2]:text-sm [&>ul_li_button_h2]:desktop:text-base ',
                              '[&.isOpenAccordion>ul_li_button]:rounded-b-none '
                            )}>
                            <Accordion items={[{ title: 'Análise do Canaltech', content: <AccordionComponent produto={produto} /> }]} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="z-[2] order-first h-full w-full self-start desktop:order-last desktop:w-[320px]">
                <div className="sticky top-2">
                  <h2 className="mb-6 mt-[4rem] text-lg font-semibold text-mglGray800">Índice</h2>
                  <ul className="mb-4 flex w-full list-none flex-col rounded-md border-0 border-l-2 border-solid border-mglGray200 bg-black/5 bg-white py-4 desktop:w-[320px]">
                    {guiaData.itens.categorias.map(categoria => (
                      <li
                        key={categoria.titulo}
                        className={
                          'mobile-menu-item menu-item-has-children relative -ml-[2px] flex py-2' +
                          (isActive === categoria.titulo.replaceAll(' ', '_') ? ' sub-menu-open border-0 border-l-2 border-solid border-mglBlue400' : '')
                        }
                        onClick={e => {
                          e.preventDefault();
                          setIsProgrammaticScroll(true);
                          toggleActive(categoria.titulo.replaceAll(' ', '_'));

                          document.getElementById(categoria.titulo.replaceAll(' ', '_')).scrollIntoView({ behavior: 'smooth' });

                          setTimeout(() => {
                            setIsProgrammaticScroll(false);
                          }, 500);
                        }}>
                        <a href={`#${categoria.titulo.replaceAll(' ', '_')}`} className="w-full pl-2 text-mglBlue400 visited:text-mglBlue600">
                          {categoria.titulo}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      </Layout>
    </>
  );
}

function AccordionComponent({ produto }) {
  const endpointImages = publicRuntimeConfig.NEXT_PUBLIC_URL_BUCKET;

  const extractYouTubeVideoID = url => {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  return (
    <div
      className={classNames(
        'relative rounded-b-md bg-white p-4',
        '[&_.pros-cons-section]:flex-col desktop:[&_.pros-cons-section]:flex-row',
        '[&_.pros-cons]:bg-mglGray100',
        '[&_.pros-cons-div-title]:rounded-t-sm [&_.pros-cons-div-title]:bg-mglGray200',
        '[&_.pros-cons_ul]:rounded-b-md [&_.pros-cons_ul]:bg-white',
        '[&_.pros-cons_ul.list-none]:bg-mglGray100',
        '[&_.divIframeYt]:mb-0',
        '[&_>div>div>button.cursor-pointer]:bg-transparent [&_>div>div>button.cursor-pointer]:p-0'
      )}>
      {produto.componente_tipo === 'AnaliseTexto' && produto.componente_conteudo.imagem ? (
        <div className="mx-0 mb-6 mt-0 w-full">
          <img src={endpointImages + produto.componente_conteudo.imagem} className="w-full rounded-md" />
        </div>
      ) : null}
      {produto.componente_tipo === 'AnaliseVideo' && produto.componente_conteudo.youtube ? (
        <div className="mb-6">
          <IFrameYoutube videoId={extractYouTubeVideoID(produto.componente_conteudo.youtube)} videoTitle={''} />
        </div>
      ) : null}
      {produto.componente_conteudo.pros.length > 0 && produto.componente_conteudo.contras.length > 0 ? (
        <ProsAndCons pros={produto.componente_conteudo.pros} cons={produto.componente_conteudo.contras} />
      ) : null}
      {produto.componente_conteudo.link_analise ? (
        <Link href={produto.componente_conteudo.link_analise}>
          <a className="flex h-7 items-center text-mglBlue400 underline visited:text-mglBlue600" target="_blank">
            <Icon icon="mdi:arrow-right" color="#0086FF" height={24} width={24} className="rounded-full border border-mglGray200" />
            Leia a matéria completa
          </a>
        </Link>
      ) : null}
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { data } = await guia.get(`/guiadecompras/`);
  const guiaData = data;

  return {
    props: {
      guiaData,
    },
  };
};
